import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { array, number, object, string } from "yup";

export type TriggerType =
	| "status"
	| "specific_date"
	| "register"
	| "order"
	| "cancel"
	| "sent_message"
	| "deal_update"
	| "assign_partner"
	| "status_back"
	| "complete_estimation"
	| "complete_inspection_reports"
	| "complete_completion_reports"
	| "cancel_back"
	| "cancel_rejected"
	| "unassign_origin_vendor"
	| "canceled_deal_reassign"
	| "register_deal_vendor_note"
	| "request_estimation"
	| "accept_estimation"
	| "reject_estimation"
	| "cancel_estimation"
	| "todo_assign_staff";
export type TriggerDateType =
	| "inspection_date"
	| "survey_date"
	| "construction_start_date"
	| "construction_end_date"
	| "todo_due_date";
type NotifyType = "immediate" | "reservation";
type SendType = "mail" | "slack" | "line";
export type AddressType = "deal_staff" | "store";
type DealType = "all" | "restoration" | "repair" | "other";
export type NotificationDealStateFormName =
	| "dealEstimationState"
	| "dealInspectionReportState"
	| "dealCompletionReportState"
	| "dealConstructionScheduleState";
export type NotificationDealState = "no_setting" | "yet" | "registered";
export type ReceiveType =
	| "all"
	| "origin_deal_vendor_company"
	| "sub_deal_vendor_company";
export type Notification = {
	content: string | null;
	dealCompletionReportState: NotificationDealState;
	dealConstructionScheduleState: NotificationDealState;
	dealEstimationState: NotificationDealState;
	dealInspectionReportState: NotificationDealState;
	dealSubtypeId: number | null;
	dealType: DealType;
	id: number;
	notificationLineSettings: DeepReadonly<NotificationLineSetting[]>;
	notificationMails: DeepReadonly<NotificationMail[]>;
	notifyReservationDays: number | null;
	notifyType: NotifyType;
	receiveType: ReceiveType;
	resend: boolean;
	sendType: SendType;
	slackIconEmoji: string | null;
	slackUserName: string | null;
	slackWebhookUrl: string | null;
	title: string | null;
	triggerDateType: TriggerDateType | null;
	triggerStatusId: number | null;
	triggerType: TriggerType;
};

export type NotificationEdit = Omit<Notification, "id" | "triggerType"> & {
	triggerType: TriggerType | null;
};

export type NotificationMail = {
	addressType: AddressType;
	id?: number;
};

export type NotificationLineSetting = {
	lineUserAccountId: number;
};

export type NotificationTargetType =
	| "CompanyNotification"
	| "VendorNotification";

export type TriggerTypeSelectOption = Readonly<{
	label: string;
	value: TriggerType;
}>;

export const buildInitialNotification = (): NotificationEdit => ({
	content: "",
	dealCompletionReportState: "no_setting",
	dealConstructionScheduleState: "no_setting",
	dealEstimationState: "no_setting",
	dealInspectionReportState: "no_setting",
	dealSubtypeId: null,
	dealType: "all",
	notificationLineSettings: [],
	notificationMails: [{ addressType: "deal_staff" }, { addressType: "store" }],
	notifyReservationDays: null,
	notifyType: "immediate",
	receiveType: "all",
	resend: true,
	sendType: "mail",
	slackIconEmoji: "",
	slackUserName: "",
	slackWebhookUrl: "",
	title: "",
	triggerDateType: null,
	triggerStatusId: null,
	triggerType: null
});

/** TODO関連の通知トリガーかどうか */
export const isTodoNotification = (
	triggerType: TriggerType | null,
	triggerDateType: TriggerDateType | null
): boolean =>
	triggerType === "todo_assign_staff" ||
	(triggerType === "specific_date" && triggerDateType === "todo_due_date");

export const createSchema = (type: NotificationTargetType) =>
	object().shape({
		dealSubtypeId: number()
			.nullable()
			.when(["dealType"], ([dealType], schema) =>
				dealType === "other" && type === "CompanyNotification"
					? schema
							.transform((value, _, context) =>
								context.isType(value) ? value : undefined
							)
							.required("入力してください")
					: schema
			),
		dealType: string().nullable().required("入力してください"),
		notificationLineSettings: array().when("sendType", {
			is: "line",
			// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
			then: (schema) =>
				schema
					.required("1つ以上選択してください")
					.min(1, "1つ以上選択してください")
		}),
		notificationMails: array().when(
			["sendType", "triggerType", "triggerDateType"],
			{
				is: (
					sendType: string,
					triggerType: TriggerType,
					triggerDateType: TriggerDateType
				) =>
					sendType === "mail" &&
					!isTodoNotification(triggerType, triggerDateType),
				// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
				then: (schema) => schema.min(1, "1つ以上選択してください")
			}
		),
		notifyReservationDays: number()
			.nullable()
			.typeError("数値で入力してください")
			.when("notifyType", {
				is: "reservation",
				// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
				then: (schema) =>
					schema.required("入力してください").when("triggerType", {
						is: "specific_date",
						otherwise: (requiredSchema) =>
							requiredSchema.min(1, "1以上の数値を入力してください")
					})
			}),
		notifyType: string().nullable().required("入力してください"),
		receiveType: string().nullable().required("入力してください"),
		sendType: string().nullable().required("入力してください"),
		slackIconEmoji: string()
			.nullable()
			.when("sendType", {
				is: "slack",
				// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
				then: (schema) => schema.required("入力してください")
			}),
		slackUserName: string()
			.nullable()
			.when("sendType", {
				is: "slack",
				// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
				then: (schema) => schema.required("入力してください")
			}),
		slackWebhookUrl: string()
			.nullable()
			.when("sendType", {
				is: "slack",
				// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
				then: (schema) => schema.required("入力してください")
			}),
		triggerDateType: string()
			.nullable()
			.when("triggerType", {
				is: "specific_date",
				// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
				then: (schema) => schema.required("入力してください")
			}),
		triggerStatusId: number()
			.nullable()
			.when("triggerType", {
				is: "status",
				// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
				then: (schema) =>
					schema
						.transform((value, _, context) =>
							context.isType(value) ? value : undefined
						)
						.required("入力してください")
			}),
		triggerType: string().nullable().required("入力してください")
	});
export const DEAL_TYPE_LABELS = {
	all: "すべて",
	other: "その他",
	repair: "修繕",
	restoration: "原状回復"
} as const satisfies Record<DealType, string>;

export const COMPANY_TRIGGER_TYPE_SELECT_OPTIONS = [
	{ label: "ステータス進行", value: "status" },
	{ label: "チャット投稿", value: "sent_message" },
	{ label: "案件メモ登録", value: "register_deal_vendor_note" },
	{ label: "案件登録", value: "register" },
	{ label: "施工会社選択", value: "order" },
	{ label: "特定日付入力・指定", value: "specific_date" },
	{ label: "取り下げ申請", value: "cancel" },
	{ label: "取り下げ申請差し戻し", value: "cancel_rejected" },
	{ label: "見積変更申請", value: "request_estimation" },
	{ label: "見積変更申請承認", value: "accept_estimation" },
	{ label: "見積変更申請棄却", value: "reject_estimation" },
	{ label: "見積変更申請取り下げ", value: "cancel_estimation" },
	{ label: "TODO担当者設定・変更", value: "todo_assign_staff" }
] as const satisfies readonly TriggerTypeSelectOption[];

export const VENDOR_TRIGGER_TYPE_SELECT_OPTIONS = [
	{ label: "ステータス進行", value: "status" },
	{ label: "ステータス戻し", value: "status_back" },
	{ label: "案件情報更新", value: "deal_update" },
	{ label: "チャット投稿", value: "sent_message" },
	{ label: "施工会社選択", value: "order" },
	{ label: "実施会社選択", value: "assign_partner" },
	{ label: "特定日付入力・指定", value: "specific_date" },
	{ label: "見積入力完了", value: "complete_estimation" },
	{ label: "立会画像登録完了", value: "complete_inspection_reports" },
	{ label: "完了画像登録完了", value: "complete_completion_reports" },
	{ label: "施工会社選択解除", value: "unassign_origin_vendor" },
	{ label: "取り下げ申請", value: "cancel" },
	{ label: "取り下げ申請キャンセル", value: "cancel_back" },
	{ label: "取り下げ済み案件再依頼", value: "canceled_deal_reassign" },
	{ label: "見積変更申請", value: "request_estimation" },
	{ label: "見積変更申請承認", value: "accept_estimation" },
	{ label: "見積変更申請棄却", value: "reject_estimation" },
	{ label: "見積変更申請取り下げ", value: "cancel_estimation" }
] as const satisfies readonly TriggerTypeSelectOption[];

export const TRIGGER_DATE_TYPE_LABELS = {
	construction_end_date: "工事完了日",
	construction_start_date: "工事開始日",
	inspection_date: "立会日程",
	survey_date: "現地調査日程",
	todo_due_date: "TODO対応期限"
} as const satisfies Record<TriggerDateType, string>;

export const NOTIFY_TYPE_LABELS = {
	immediate: "即時通知",
	reservation: "通知予約"
} as const satisfies Record<NotifyType, string>;

export const SEND_TYPE_LABELS = {
	line: "LINE",
	mail: "メール",
	slack: "Slack"
} as const satisfies Record<SendType, string>;

export const ADDRESS_TYPE_TO_LABELS = {
	deal_staff: "案件担当者",
	store: "店舗通知先"
} as const satisfies Record<AddressType, string>;

export const DEAL_STATE_NAMES = {
	dealCompletionReportState: "完了報告画像",
	dealConstructionScheduleState: "工事日程の入力状況",
	dealEstimationState: "見積り",
	dealInspectionReportState: "立会報告画像"
} as const satisfies Record<NotificationDealStateFormName, string>;

export const DEAL_STATE_LABELS = {
	no_setting: "登録状況に関わらず通知",
	registered: "登録済みのみ",
	yet: "未登録のみ"
} as const satisfies Record<NotificationDealState, string>;

export const RECEIVE_TYPE_LABELS = {
	all: "すべての案件",
	origin_deal_vendor_company: "施工会社のときのみ",
	sub_deal_vendor_company: "実施会社のときのみ"
} as const satisfies Record<ReceiveType, string>;

export const ESTIMATION_DESCRIPTIONS = {
	no_setting: "",
	registered: "見積りが1件以上登録されている場合に通知されます",
	yet: "見積りが登録されていない場合に通知されます"
} as const satisfies Record<NotificationDealState, string>;

export const REPORT_DESCRIPTIONS = {
	no_setting: "",
	registered: "画像が1件以上登録されている場合に通知されます",
	yet: "画像が登録されていない場合に通知されます"
} as const satisfies Record<NotificationDealState, string>;

export const SCHEDULE_DESCRIPTIONS = {
	no_setting: "",
	registered: "いずれかの日程が登録されている場合に通知されます",
	yet: "日程が一つも登録されていない場合に通知されます"
} as const satisfies Record<NotificationDealState, string>;

export const ONLY_IMMEDIATE_TRIGGER_TYPE = [
	"sent_message",
	"deal_update",
	"assign_partner",
	"status_back",
	"complete_estimation",
	"complete_inspection_reports",
	"complete_completion_reports",
	"cancel_back",
	"cancel_rejected",
	"unassign_origin_vendor",
	"canceled_deal_reassign",
	"register_deal_vendor_note",
	"request_estimation",
	"accept_estimation",
	"reject_estimation",
	"cancel_estimation",
	"todo_assign_staff"
];

export const ONLY_ORIGIN_VENDOR_TRIGGER_TYPE = [
	"order",
	"complete_estimation",
	"complete_inspection_reports",
	"complete_completion_reports",
	"unassign_origin_vendor",
	"cancel_estimation"
];

export const ONLY_SUB_VENDOR_TRIGGER_TYPE = ["assign_partner"];
